import { Form } from 'antd';
import { DarwinCol, DarwinFlex } from 'components/darwin-ui';
import { DarwinModal } from 'components/darwin-ui/Modal';
import { GenericInput, GenericInputProps } from '../GenericInput';
import { ErrorType, InputType } from 'types/genericTypes';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import {
  ProcessField,
  ProcessForm,
  CustomProcessField,
} from 'types/dataTableTypes';
import styles from './index.module.css';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useEffect, useState, useMemo } from 'react';
import DarwinTypography from 'components/darwin-ui/Typography';
import {
  DATA_TABLE_FILED_TYPE,
  FIELD_TYPES_WITHOUT_LENGTH,
} from 'constants/dataTable';

export type FormFields = {
  fieldName: string;
  fieldType: string;
  length?: string;
  description: string;
  formIds: string[];
};

const FIELD_DESCRIPTION_MAX_LENGTH = 500;
const TEXT_FIELD_MAX_LENGTH = 50;

// Field name constants for type safety
const FIELD_NAMES = {
  fieldName: 'fieldName',
  fieldType: 'fieldType',
  length: 'length',
  description: 'description',
  required: 'required',
  formIds: 'formIds',
} as const;

interface FieldsModalProps {
  initialField?: ProcessField | CustomProcessField;
  type: 'add' | 'edit';
  open: boolean;
  closeModal: () => void;
  onSubmit?: (data: FormFields) => Promise<any>;
  formsData?: ProcessForm[];
}

const defaultValuesForm = {
  fieldName: '',
  fieldType: '',
  length: '',
  description: '',
  required: false,
  formIds: [],
};

export const FieldsModal = ({
  initialField,
  type = 'add',
  open,
  closeModal,
  onSubmit,
  formsData = [],
}: FieldsModalProps) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const [initialValuesOnEditForm, setInitialValuesOnEditForm] = useState({});

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      ...defaultValuesForm,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (initialField) {
      const payload: FormFields = {
        fieldName: initialField?.name,
        fieldType: initialField?.type.toLowerCase(),
        length: initialField?.length?.toString() || '',
        description: initialField?.description || '',
        formIds: initialField?.formIds || [], // Use formIds from initialField if available
      };
      reset(payload);
      setInitialValuesOnEditForm(payload);
    } else {
      reset(defaultValuesForm);
    }
  }, [initialField, open, reset]);

  //Logic for disabled button
  const currentValues = useWatch({ control });

  const isEditForm =
    JSON.stringify(currentValues) === JSON.stringify(initialValuesOnEditForm);

  const isNewForm =
    JSON.stringify(currentValues) === JSON.stringify(defaultValuesForm);

  const disableButton = isEditForm || isNewForm;

  const onFormSubmit = async (data: FormFields) => {
    try {
      await onSubmit?.(data);

      openNotification(
        t('success'),
        t('data-tables.field-modal.submit-success', {
          action:
            type === 'add'
              ? t('data-tables.field-modal.actions.added')
              : t('data-tables.field-modal.actions.edited'),
        }),
        'success'
      );
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('data-tables.field-modal.submit-error', {
            action:
              type === 'add'
                ? t('data-tables.field-modal.actions.add')
                : t('data-tables.field-modal.actions.edit'),
          }),
          'error'
        );
      }
    } finally {
      closeModal();
    }
  };

  const onCancel = () => {
    reset();
    closeModal();
  };

  const fieldTypeValue = watch('fieldType');

  const dataTableInputs = useMemo(() => {
    const inputs: Omit<GenericInputProps<FormFields>, 'control' | 'errors'>[] =
      [
        {
          inputType: InputType.INPUT,
          fieldName: FIELD_NAMES.fieldName,
          label: t('data-tables.field-modal.fields.field-name'),
          showRequiredAsterisk: true,
          inputRules: {
            required: t('data-tables.field-modal.errors.required'),
          },
          maxLength: TEXT_FIELD_MAX_LENGTH,
        },
        {
          inputType: InputType.DROPDOWN,
          fieldName: FIELD_NAMES.fieldType,
          label: t('data-tables.field-modal.fields.field-type'),
          showRequiredAsterisk: true,
          inputRules: {
            required: t('data-tables.field-modal.errors.required'),
          },
          dropdownData: DATA_TABLE_FILED_TYPE.map(field => ({
            ...field,
            label: t(field.key),
          })),
        },
        ...(!FIELD_TYPES_WITHOUT_LENGTH.includes(fieldTypeValue.toLowerCase())
          ? [
              {
                inputType: InputType.INPUT,
                fieldName: FIELD_NAMES.length,
                label: t('data-tables.field-modal.fields.length'),
                inputRules: {
                  pattern: {
                    value: /^(?!0$)[0-9]+$/,
                    message: t('data-tables.field-modal.errors.numbers-only'),
                  },
                },
                maxLength: TEXT_FIELD_MAX_LENGTH,
              },
            ]
          : []),
        {
          inputType: InputType.TEXTAREA,
          fieldName: FIELD_NAMES.description,
          label: t('data-tables.field-modal.fields.description'),
          showRequiredAsterisk: true,
          inputRules: {
            required: t('data-tables.field-modal.errors.required'),
          },
          maxLength: FIELD_DESCRIPTION_MAX_LENGTH,
        },
        {
          inputType: InputType.DROPDOWN,
          fieldName: FIELD_NAMES.formIds,
          label: t('data-tables.field-modal.fields.form-ids'),
          showRequiredAsterisk: true,
          inputRules: {
            required: t('data-tables.field-modal.errors.required'),
            validate: (value: any) => {
              if (!value || value.length === 0) {
                return t('data-tables.field-modal.errors.at-least-one-form');
              }
              return true;
            },
          },
          dropdownData:
            formsData?.map(form => ({
              id: form.id,
              label: form.name,
            })) || [],
          isDropdownMultiple: true,
          enableSearchInDropdown: true,
        },
      ];

    return inputs;
  }, [fieldTypeValue, t, formsData]);

  return (
    <DarwinModal
      open={open}
      title={
        <DarwinTypography.Text className={styles.modalTitle}>
          {type === 'add'
            ? t('data-tables.field-modal.add-new-field')
            : t('data-tables.field-modal.edit-field')}
        </DarwinTypography.Text>
      }
      cancelText={t('data-tables.field-modal.cancel-button')}
      okText={t('data-tables.field-modal.ok-button')}
      cancelButtonProps={{ className: styles.modalFooterButton }}
      okButtonProps={{
        disabled: !!disableButton,
        className: styles.modalFooterButton,
      }}
      onCancel={onCancel}
      onOk={handleSubmit(onFormSubmit)}
      footer={(_, { OkBtn, CancelBtn }) => (
        <DarwinFlex
          justify="center"
          align="center"
          className={styles.modalFooter}
        >
          <CancelBtn />
          <OkBtn />
        </DarwinFlex>
      )}
      centered
    >
      <Form layout="vertical" onSubmitCapture={handleSubmit(onFormSubmit)}>
        <DarwinCol>
          {dataTableInputs.map(inputProps => (
            <GenericInput<FormFields>
              key={inputProps.fieldName}
              control={control}
              errors={errors}
              {...inputProps}
            />
          ))}
        </DarwinCol>
      </Form>
    </DarwinModal>
  );
};
